import React from 'react';
import { Icon } from '../../design/Icon';
import { accountTimeZone, formatInZone, timeZoneAbbreviation } from '../../design/timeZone';

export function EmptyState({ icon = 'phone', title, body, actions }) {
  return (
    <section className="state-panel state-empty" aria-live="polite">
      <div className="s-icon">
        <Icon name={icon} />
      </div>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      {actions && <div className="s-acts">{actions}</div>}
    </section>
  );
}

// "GET /api/... · 503 Service Unavailable · Reference ... · 10:42:18 AM ET" (mockup state panels), in the account time zone.
export function describeRequestError(error, at = new Date()) {
  if (!error) return null;
  const config = error.config || {};
  const response = error.response;
  const parts = [];
  if (config.url) {
    const base = (config.baseURL || '').replace(/^https?:\/\/[^/]+/, '');
    parts.push(`${(config.method || 'get').toUpperCase()} ${base}${config.url.startsWith('/') ? '' : '/'}${config.url}`);
  }
  if (response) parts.push(`${response.status}${response.statusText ? ` ${response.statusText}` : ''}`);
  else parts.push(error.code === 'ECONNABORTED' ? 'Timed out' : 'No response');
  const reference = response?.headers?.['x-correlation-id'] || response?.headers?.['x-request-id'] || response?.data?.referenceId;
  if (reference) parts.push(`Reference ${reference}`);
  parts.push(`${formatInZone(at, { hour: 'numeric', minute: '2-digit', second: '2-digit' })} ${timeZoneAbbreviation(accountTimeZone(), at)}`);
  return parts.join(' · ');
}

export function ErrorState({
  title = "This view couldn't load",
  body = "The analytics service didn't respond. Your data is safe; retry, or check service status if this continues.",
  error,
  diagnostics,
  onRetry,
  actions,
}) {
  const diag = diagnostics || describeRequestError(error);
  return (
    <section className="state-panel state-error" role="alert">
      <div className="s-icon">
        <Icon name="alert" />
      </div>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      <div className="s-acts">
        {onRetry && (
          <button type="button" className="btn btn-primary" onClick={onRetry}>
            <Icon name="refresh" />
            Retry
          </button>
        )}
        {actions}
      </div>
      {diag && <div className="s-diag">{diag}</div>}
    </section>
  );
}

export function Skeleton({ height = 120, className = '' }) {
  return (
    <div className={`is-loading ${className}`.trim()} aria-busy="true">
      <div className="skel" style={{ height }} />
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <span className="row" role="status">
      <span className="spinner" aria-hidden="true" />
      {label && <span className="caption">{label}</span>}
    </span>
  );
}
